import React from "react";
import DatasourceCardLayout from "../../../shared/cards/DatasourceCardLayout";
import AccordionLayout, { accordionExpandedCacheKey } from "../../../shared/accordions/AccordionLayout";
import { workbookCache } from "../../../../excel/workbook/cache";
import { useProject } from "../../../context/ProjectContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useBanner } from "../../../context/BannerContext";
import { EDIPHI_DATASOURCE_META } from "../meta";
import {
  LAST_REFRESHED,
  LAST_REFRESHED_FORMAT,
  CONTRACTING_REMOVED_DATA_REMAINS,
  EDIPHI_DATASOURCE_DELETE_NOTICE,
} from "../../../constants";
import { formatCurrency, formatCompactCurrency } from "../format";
import { openContractingInEdiphi } from "../../../../utils/links";
import { clearReportWorksheets } from "../../../../excel/reports/api/registry";
import { reportTypeOfTemplate } from "../../../hooks/useReportCards";
import { useContractingLoader } from "../../../hooks/useContractingLoader";
import { useCardSummary } from "../../../hooks/useCardSummary";

const DETAILS_CACHE_KEY = accordionExpandedCacheKey("contracting");

const ContractingDatasourceCard = () => {
  const { selectedProject, isContractingLoaded, setIsContractingLoaded } = useProject();
  const { removeDatasource } = useDatasources();
  const { showBanner } = useBanner();
  const { loadContracting, clearContracting } = useContractingLoader();
  const summary = useCardSummary("contracting");

  const projectId = selectedProject?.id;

  if (!isContractingLoaded) return null;

  const metricValue = (value: number | null | undefined) => (value == null ? "—" : formatCurrency(value));

  const handleRemoveCard = async () => {
    removeDatasource("ediphi", "contracting");
    setIsContractingLoaded(false);
    void workbookCache.delete(DETAILS_CACHE_KEY);

    // The React state above is already cleared, so a failure here leaves the card gone but the
    // workbook still holding this project's contracting numbers — say so rather than failing silently.
    try {
      await clearReportWorksheets((template) => reportTypeOfTemplate(template) === "contracting");
      await clearContracting();
    } catch {
      showBanner(CONTRACTING_REMOVED_DATA_REMAINS, "error");
    }
  };

  return (
    <DatasourceCardLayout
      header={{
        icon: EDIPHI_DATASOURCE_META.contracting.icon,
        text: EDIPHI_DATASOURCE_META.contracting.label,
      }}
      card={{
        text: "Contracting",
        subtext: summary
          ? `${formatCompactCurrency(summary.projectedBuy)} • ${summary.packageCount} bid package${
              summary.packageCount === 1 ? "" : "s"
            }`
          : "",
      }}
      onLaunch={() => openContractingInEdiphi(projectId as string)}
      onRefresh={() => void loadContracting()}
      onClose={handleRemoveCard}
      deleteNotice={EDIPHI_DATASOURCE_DELETE_NOTICE}
    >
      <AccordionLayout
        details={[
          { label: "Projected Buy", value: metricValue(summary?.projectedBuy) },
          { label: "Budget", value: metricValue(summary?.budget) },
          { label: "Buy Value", value: metricValue(summary?.buyValue) },
          { label: "Bid Packages", value: summary ? String(summary.packageCount) : "—" },
          { label: "Awarded", value: summary ? `${summary.awardedCount} of ${summary.packageCount}` : "—" },
        ]}
        lastUpdated={summary?.lastRefreshedAt}
        lastUpdatedLabel={LAST_REFRESHED}
        lastUpdatedFormat={LAST_REFRESHED_FORMAT}
        cacheKey={DETAILS_CACHE_KEY}
      />
    </DatasourceCardLayout>
  );
};

export default ContractingDatasourceCard;
